/**
 * CLI session command — `canary session <subcommand>`.
 *
 * Flat subcommands for session lifecycle and browser tool execution.
 * All commands delegate to the session daemon via HTTP.
 *
 * @module
 */

import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import process from 'node:process';
import { getArgValue, hasFlag, resolveConfig } from '../auth.js';
import {
  createSession,
  listSessions,
  getSession,
  deleteSession,
  deleteAllSessions,
  swapSessionContext,
  getSessionStorageState,
  callTool,
  resolveTargetSession,
  takeControl,
  resumeCloud,
} from './daemon-client.js';
import {
  findClaudeSettingsDir,
  hasScreenshotPermission,
  addScreenshotPermission,
  getScreenshotReadPattern,
} from './claude-settings.js';
import { selectCredential, uploadStorageState } from '../cli-helpers.js';
import { attachCliDiagnosticMetadata } from '../cli-diagnostics.js';
import { resolveCredential } from './credentials.js';
import { runDaemon } from './daemon.js';
import { handleCallRecipe } from './call-recipe.js';
import type { SessionInfo, ToolResponse } from './types.js';

/** Predictable screenshot directory under the user's home. */
function getScreenshotDir() {
  return path.join(os.homedir(), '.canary', 'screenshots');
}

/** Delete all screenshot files (but keep the directory). */
function cleanupScreenshots() {
  const dir = getScreenshotDir();
  if (!fs.existsSync(dir)) return;
  for (const file of fs.readdirSync(dir)) {
    fs.unlinkSync(path.join(dir, file));
  }
}

/** Delete screenshot files older than 1 hour (stale from crashed sessions). */
function cleanupStaleScreenshots() {
  const dir = getScreenshotDir();
  if (!fs.existsSync(dir)) return;
  const cutoff = Date.now() - 60 * 60 * 1000;
  for (const file of fs.readdirSync(dir)) {
    const filePath = path.join(dir, file);
    try {
      const stat = fs.statSync(filePath);
      if (stat.isFile() && stat.mtimeMs < cutoff) {
        fs.unlinkSync(filePath);
      }
    } catch {
      // Ignore races
    }
  }
}

/* ── Output formatting ───────────────────────────────────────────────── */

function formatDuration(since: string): string {
  const seconds = Math.floor((Date.now() - new Date(since).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function printSessionTable(sessions: SessionInfo[]) {
  if (sessions.length === 0) {
    console.log('No active sessions.');
    return;
  }

  const header = ['ID', 'NAME', 'MODE', 'CREDENTIAL', 'URL', 'UPTIME', 'IDLE'];
  const rows = sessions.map((s) => [
    s.id,
    s.name,
    s.mode,
    s.credentialName ?? '(none)',
    s.url ?? '(none)',
    formatDuration(s.startedAt),
    formatDuration(s.lastActivityAt),
  ]);

  // Calculate column widths
  const widths = header.map((h, i) =>
    Math.max(h.length, ...rows.map((r) => r[i].length))
  );

  const pad = (str: string, width: number) => str.padEnd(width);
  console.log(header.map((h, i) => pad(h, widths[i])).join('  '));
  for (const row of rows) {
    console.log(row.map((cell, i) => pad(cell, widths[i])).join('  '));
  }
}

function printToolResult(result: ToolResponse, jsonMode: boolean) {
  if (jsonMode) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exitCode = 1;
    return;
  }

  if (result.text) {
    console.log(result.text);
  }

  if (result.images?.length) {
    const screenshotDir = getScreenshotDir();
    fs.mkdirSync(screenshotDir, { recursive: true });
    // Server-produced, local-only images — but cap the decode as defense-in-depth
    // against a misbehaving image pipeline sending an implausibly large payload.
    const MAX_IMAGE_BYTES = 32 * 1024 * 1024;
    for (const img of result.images) {
      if (typeof img.data !== 'string' || img.data.length > MAX_IMAGE_BYTES) {
        console.error(`Skipped an image: payload too large or malformed (${img.data?.length ?? 0} chars).`);
        continue;
      }
      const ext = img.mimeType === 'image/png' ? 'png' : 'jpg';
      const filePath = path.join(screenshotDir, `screenshot-${Date.now()}.${ext}`);
      fs.writeFileSync(filePath, Buffer.from(img.data, 'base64'));
      console.log(`Screenshot saved: ${filePath}`);
    }
  }
}

/* ── Subcommand: start ───────────────────────────────────────────────── */

async function handleStart(argv: string[]) {
  // Clean up stale screenshots from crashed/killed sessions
  cleanupStaleScreenshots();

  const headless = hasFlag(argv, '--headless');
  const name = getArgValue(argv, '--name');
  const url = getArgValue(argv, '--url');
  const storageStatePath = getArgValue(argv, '--storage-state');
  const jsonMode = hasFlag(argv, '--json');
  const attachRealChrome = hasFlag(argv, '--attach-real-chrome');

  if (attachRealChrome) {
    const conflicts: string[] = [];
    if (headless) conflicts.push('--headless');
    if (storageStatePath) conflicts.push('--storage-state');
    if (url) conflicts.push('--url');
    if (argv.includes('--credential')) conflicts.push('--credential');
    if (argv.includes('--cloud-relay')) conflicts.push('--cloud-relay');
    if (conflicts.length > 0) {
      console.error(
        `Error: --attach-real-chrome cannot be combined with: ${conflicts.join(', ')}`,
      );
      console.error('Attached mode uses the live browser\'s profile and tabs.');
      process.exitCode = 1;
      return;
    }

    const result = await createSession({ attachRealChrome: true, name });
    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }
    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      console.error(
        'Hint: in your real Chrome, open chrome://inspect/#remote-debugging and approve remote debugging (Chrome 144+).',
      );
      process.exitCode = 1;
      return;
    }
    const session = result.data!;
    console.log(`Session "${session.id}" attached to your real Chrome (M144+).`);
    console.log('  Chrome is showing the "controlled by automated test software" banner.');
    console.log(`  Stop with: canary session stop ${session.id}`);
    return;
  }

  // Credential handling
  const credentialIdx = argv.indexOf('--credential');
  let resolvedCred: { storageStatePath: string | undefined; credentialName: string } | null = null;

  if (credentialIdx !== -1) {
    // Check if next arg is a value or another flag
    const nextArg = argv[credentialIdx + 1];
    const credentialArg = nextArg && !nextArg.startsWith('--') ? nextArg : undefined;
    resolvedCred = await resolveCredential(argv, credentialArg);
    if (!resolvedCred) {
      process.exitCode = 1;
      return;
    }
  }

  // Cloud relay setup
  let cloudRelay: { apiUrl: string; token: string } | undefined;
  if (hasFlag(argv, '--cloud-relay')) {
    const config = await resolveConfig(argv);
    cloudRelay = { apiUrl: config.apiUrl, token: config.token };
  }

  const result = await createSession({
    headless,
    name,
    url,
    storageStatePath: resolvedCred?.storageStatePath ?? storageStatePath,
    credentialName: resolvedCred?.credentialName,
    cloudRelay,
  });

  if (jsonMode) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exitCode = 1;
    return;
  }

  const session = result.data!;
  console.log(`Session "${session.id}" started (${session.mode})`);
  if (session.credentialName) {
    console.log(`  Credential: ${session.credentialName}`);
  }
  // Show the requested URL if provided, since the daemon responds before navigation completes
  const displayUrl = url ?? session.url;
  if (displayUrl) {
    console.log(`  URL: ${displayUrl}`);
  }
  if (cloudRelay) {
    console.log(`  Cloud relay: connecting...`);
  }

  // Hint about screenshot permissions for Claude Code users
  if (!jsonMode) {
    const settingsDir = findClaudeSettingsDir();
    if (settingsDir && !hasScreenshotPermission(settingsDir)) {
      console.log('');
      console.log('  Tip: Claude needs permission to read screenshots.');
      console.log('  Run: canary session allow-screenshots');
    }
  }
}

/* ── Subcommand: list ────────────────────────────────────────────────── */

async function handleList(argv: string[]) {
  const jsonMode = hasFlag(argv, '--json');
  const result = await listSessions();

  if (jsonMode) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exitCode = 1;
    return;
  }

  printSessionTable(result.data ?? []);
}

/* ── Subcommand: status ──────────────────────────────────────────────── */

async function handleStatus(argv: string[]) {
  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);
  const jsonMode = hasFlag(argv, '--json');

  try {
    const target = await resolveTargetSession(sessionId);
    const result = await getSession(target.id);

    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    if (!result.ok || !result.data) {
      console.error(`Error: ${result.error}`);
      process.exitCode = 1;
      return;
    }

    const s = result.data;
    console.log(`Session: ${s.id} (${s.name})`);
    console.log(`  Mode: ${s.mode}`);
    console.log(`  Credential: ${s.credentialName ?? '(none)'}`);
    console.log(`  URL: ${s.url ?? '(none)'}`);
    console.log(`  Controller: ${s.lock.controller}${s.lock.cloudPaused ? ' (cloud paused)' : ''}`);
    if (s.cloudRelay) {
      console.log(`  Cloud relay: ${s.cloudRelay.connected ? 'connected' : 'disconnected'}`);
    }
    console.log(`  Uptime: ${formatDuration(s.startedAt)}`);
    console.log(`  Idle: ${formatDuration(s.lastActivityAt)}`);
    console.log(`  Started: ${s.startedAt}`);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/* ── Subcommand: stop ────────────────────────────────────────────────── */

async function handleStop(argv: string[]) {
  const jsonMode = hasFlag(argv, '--json');

  if (hasFlag(argv, '--all')) {
    const result = await deleteAllSessions();
    if (result.ok) cleanupScreenshots();
    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
    } else if (result.ok) {
      console.log('All sessions stopped.');
    } else {
      console.error(`Error: ${result.error}`);
      process.exitCode = 1;
    }
    return;
  }

  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);
  const sessionTargetSource = getArgValue(argv, '--session')
    ? 'flag'
    : sessionId
      ? 'positional'
      : 'auto';

  try {
    let target: SessionInfo;
    try {
      target = await resolveTargetSession(sessionId);
    } catch (err) {
      throw attachCliDiagnosticMetadata(err, {
        cliDaemonOperation: 'session.stop',
        cliDaemonPhase: 'resolve-target',
        cliSessionTargetSource: sessionTargetSource,
        cliSessionRequestedTarget: sessionId ?? null,
      });
    }

    const result = await deleteSession(target.id, { sessionTargetSource });

    if (result.ok) {
      // Clean up screenshots if no sessions remain
      const remaining = await listSessions();
      if (remaining.ok && (!remaining.data || remaining.data.length === 0)) {
        cleanupScreenshots();
      }
    }

    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
    } else if (result.ok) {
      console.log(`Session "${target.id}" stopped.`);
    } else {
      console.error(`Error: ${result.error}`);
      process.exitCode = 1;
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/* ── Subcommand: load-credential ────────────────────────────────────── */

async function handleLoadCredential(argv: string[]) {
  const jsonMode = hasFlag(argv, '--json');
  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);

  // Resolve which session to target
  let target;
  try {
    target = await resolveTargetSession(sessionId);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
    return;
  }

  // Credential selection (interactive by default, or --credential <name|id>)
  const credentialIdx = argv.indexOf('--credential');
  const nextArg = credentialIdx !== -1 ? argv[credentialIdx + 1] : undefined;
  const credentialArg = nextArg && !nextArg.startsWith('--') ? nextArg : undefined;

  // Also support --storage-state for local files
  const storageStatePath = getArgValue(argv, '--storage-state');

  let resolvedStorageStatePath: string | undefined;
  let credentialName: string | undefined;

  if (storageStatePath) {
    resolvedStorageStatePath = storageStatePath;
    credentialName = '(local storage state)';
  } else {
    // Interactive or named credential resolution
    const resolvedCred = await resolveCredential(argv, credentialArg);
    if (!resolvedCred) {
      process.exitCode = 1;
      return;
    }
    resolvedStorageStatePath = resolvedCred.storageStatePath;
    credentialName = resolvedCred.credentialName;
  }

  const result = await swapSessionContext(target.id, {
    storageStatePath: resolvedStorageStatePath,
    credentialName,
  });

  if (jsonMode) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exitCode = 1;
    return;
  }

  const session = result.data!;
  console.log(`Credential loaded into session "${session.id}".`);
  if (session.credentialName) {
    console.log(`  Credential: ${session.credentialName}`);
  }
  if (session.url) {
    console.log(`  URL: ${session.url}`);
  }
}

/* ── Subcommand: save-credential ───────────────────────────────────── */

async function handleSaveCredential(argv: string[]) {
  const jsonMode = hasFlag(argv, '--json');
  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);

  let target: SessionInfo;
  try {
    target = await resolveTargetSession(sessionId);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);
  const credentialIdx = argv.indexOf('--credential');
  const nextArg = credentialIdx !== -1 ? argv[credentialIdx + 1] : undefined;
  const credentialHint = nextArg && !nextArg.startsWith('--') ? nextArg : undefined;

  // If no --credential flag, fall back to the session's credential name
  const credential = await selectCredential(apiUrl, token, credentialHint ?? target.credentialName);
  if (!credential) {
    console.error('Could not resolve credential. Use --credential <name|id>.');
    process.exitCode = 1;
    return;
  }

  const storageResult = await getSessionStorageState(target.id);
  if (!storageResult.ok || !storageResult.data) {
    const msg = storageResult.error ?? 'Unknown error';
    if (jsonMode) {
      console.log(JSON.stringify({ ok: false, error: msg }));
    } else {
      console.error(`Failed to extract storage state: ${msg}`);
    }
    process.exitCode = 1;
    return;
  }

  const uploadResult = await uploadStorageState({
    apiUrl,
    token,
    propertyId: credential.propertyId,
    credentialId: credential.id,
    storageState: storageResult.data,
  });

  if (jsonMode) {
    console.log(JSON.stringify(uploadResult));
    return;
  }

  if (!uploadResult.ok) {
    console.error(`Failed to save: ${uploadResult.error ?? 'Unknown error'}`);
    process.exitCode = 1;
    return;
  }

  console.log(`Credential "${credential.name}" storage state saved.`);
}

/* ── Subcommand: allow-screenshots ──────────────────────────────────── */

function handleAllowScreenshots() {
  const settingsDir = findClaudeSettingsDir();
  if (!settingsDir) {
    console.error(
      'Error: No .claude/ directory found. Run this inside a project with Claude Code configured.'
    );
    process.exitCode = 1;
    return;
  }

  const result = addScreenshotPermission(settingsDir);
  if (result.alreadyPresent) {
    console.log('Screenshot read permission already configured.');
    console.log(`  Pattern: ${getScreenshotReadPattern()}`);
  } else {
    console.log('Added screenshot read permission to .claude/settings.local.json:');
    console.log(`  ${result.added}`);
  }
}

/* ── Subcommand: take-control ───────────────────────────────────────── */

async function handleTakeControl(argv: string[]) {
  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);
  const jsonMode = hasFlag(argv, '--json');

  try {
    const target = await resolveTargetSession(sessionId);
    const result = await takeControl(target.id);

    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      process.exitCode = 1;
      return;
    }

    const session = result.data!;
    console.log(`CLI now controls session "${session.id}".`);
    if (session.lock.cloudPaused) {
      console.log('  Cloud execution paused. Resume with: canary session resume-cloud');
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/* ── Subcommand: resume-cloud ──────────────────────────────────────── */

async function handleResumeCloud(argv: string[]) {
  const sessionId =
    getArgValue(argv, '--session') ||
    (argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined);
  const jsonMode = hasFlag(argv, '--json');

  try {
    const target = await resolveTargetSession(sessionId);
    const result = await resumeCloud(target.id);

    if (jsonMode) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      process.exitCode = 1;
      return;
    }

    console.log(`Cloud execution resumed for session "${result.data!.id}".`);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

/* ── Browser tool subcommands ────────────────────────────────────────── */

async function handleToolCommand(toolName: string, argv: string[]) {
  const sessionIdOrName = getArgValue(argv, '--session');
  const jsonMode = hasFlag(argv, '--json');

  try {
    const target = await resolveTargetSession(sessionIdOrName);
    const args = buildToolArgs(toolName, argv);
    const result = await callTool(target.id, toolName, args);
    printToolResult(result, jsonMode);
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exitCode = 1;
  }
}

export function buildToolArgs(toolName: string, argv: string[]): Record<string, unknown> {
  const args: Record<string, unknown> = {};

  switch (toolName) {
    case 'navigate':
      args.url = getArgValue(argv, '--url');
      break;

    case 'snapshot':
      args.search = getArgValue(argv, '--search');
      args.expand = getArgValue(argv, '--expand');
      // --refless: fast-drive ref-less tree (no ai-mode refs, no augmentation,
      // duplicate-label disambiguation hints baked in). Wins over --mode.
      args.mode = hasFlag(argv, '--refless')
        ? 'tree-refless'
        : (getArgValue(argv, '--mode') ?? 'tree');
      break;

    case 'screenshot':
      args.fullPage = hasFlag(argv, '--full-page');
      args.label = getArgValue(argv, '--label');
      args.returnImage = true;
      break;

    case 'evaluate':
      args.function = getArgValue(argv, '--js');
      break;

    case 'run_code':
      args.code = getArgValue(argv, '--code');
      // Fast-drive observation: 'immediate' returns cheap mutation/network
      // signals (no snapshot); 'settle' waits + returns a ref-less diff.
      args.observe = getArgValue(argv, '--observe');
      break;

    case 'scan': {
      // Fast-drive perception primitive: find one precise action locator.
      args.query = getArgValue(argv, '--query');
      // Coordinate-probe: --at "x,y" (a viewport pixel to hit-test).
      const at = getArgValue(argv, '--at');
      if (at) {
        const [x, y] = at.split(',').map((n) => parseInt(n.trim(), 10));
        if (Number.isFinite(x) && Number.isFinite(y)) args.at = { x, y };
      }
      args.targetRole = getArgValue(argv, '--target-role');
      args.targetText = getArgValue(argv, '--target-text');
      args.relation = getArgValue(argv, '--relation');
      // Opt into the annotated context crop (saved as an image) so you can SEE
      // what scan matched — useful for ambiguous results and coordinate-probes.
      if (hasFlag(argv, '--visualize')) args.visualize = true;
      break;
    }

    case 'click': {
      const ref = getArgValue(argv, '--ref');
      const x = getArgValue(argv, '--x');
      const y = getArgValue(argv, '--y');
      if (ref) args.ref = ref;
      if (x) args.x = parseInt(x, 10);
      if (y) args.y = parseInt(y, 10);
      args.element = getArgValue(argv, '--element') ?? '';
      break;
    }

    case 'type':
      args.ref = getArgValue(argv, '--ref');
      args.text = getArgValue(argv, '--text');
      args.element = getArgValue(argv, '--element') ?? '';
      args.submit = hasFlag(argv, '--submit');
      break;

    case 'fill_form': {
      const fieldsStr = getArgValue(argv, '--fields');
      if (fieldsStr) {
        try {
          args.fields = JSON.parse(fieldsStr);
        } catch {
          console.error('Error: --fields must be valid JSON');
          process.exitCode = 1;
        }
      }
      break;
    }

    case 'select_option':
      args.ref = getArgValue(argv, '--ref');
      args.value = getArgValue(argv, '--value');
      args.element = getArgValue(argv, '--element') ?? '';
      break;

    case 'press_key':
      args.key = getArgValue(argv, '--key');
      break;

    case 'scroll':
      args.direction = getArgValue(argv, '--direction') ?? 'down';
      {
        const amount = getArgValue(argv, '--amount');
        if (amount) args.amount = parseInt(amount, 10);
      }
      break;

    case 'hover':
      args.ref = getArgValue(argv, '--ref');
      args.element = getArgValue(argv, '--element') ?? '';
      break;

    case 'drag': {
      // Two modes: refs (start-ref/end-ref) or coordinates (start-x/y, end-x/y).
      const startRef = getArgValue(argv, '--start-ref');
      const endRef = getArgValue(argv, '--end-ref');
      if (startRef) args.startRef = startRef;
      if (endRef) args.endRef = endRef;
      for (const [flag, key] of [
        ['--start-x', 'startX'],
        ['--start-y', 'startY'],
        ['--end-x', 'endX'],
        ['--end-y', 'endY'],
      ] as const) {
        const value = getArgValue(argv, flag);
        if (value) args[key] = parseInt(value, 10);
      }
      // startElement/endElement are required by the tool schema.
      args.startElement = getArgValue(argv, '--start-element') ?? '';
      args.endElement = getArgValue(argv, '--end-element') ?? '';
      break;
    }

    case 'tabs': {
      args.action = getArgValue(argv, '--action') ?? 'list';
      const index = getArgValue(argv, '--index');
      if (index) args.index = parseInt(index, 10);
      break;
    }

    case 'wait_for': {
      args.text = getArgValue(argv, '--text');
      const timeout = getArgValue(argv, '--timeout');
      if (timeout) args.timeout = parseInt(timeout, 10);
      break;
    }

    case 'console_messages':
      args.onlyErrors = hasFlag(argv, '--only-errors');
      break;

    case 'file_upload': {
      const paths = getArgValue(argv, '--paths');
      args.paths = paths
        ? paths
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean)
        : [];
      break;
    }

    case 'resize': {
      const width = getArgValue(argv, '--width');
      const height = getArgValue(argv, '--height');
      if (width) args.width = parseInt(width, 10);
      if (height) args.height = parseInt(height, 10);
      break;
    }

    case 'dismiss_overlay': {
      const strategy = getArgValue(argv, '--strategy');
      if (strategy) args.preferredStrategy = strategy;
      break;
    }

    case 'handle_dialog': {
      args.action = getArgValue(argv, '--action') ?? 'accept';
      const promptText = getArgValue(argv, '--prompt-text');
      if (promptText) args.promptText = promptText;
      break;
    }

    case 'read_download': {
      const id = getArgValue(argv, '--id');
      if (id) args.downloadId = parseInt(id, 10);
      break;
    }

    case 'pdf_read':
      args.url = getArgValue(argv, '--url');
      break;

    // navigate_back, network_requests, close, list_downloads
    // have no special args or use defaults
  }

  return args;
}

/* ── Command aliases ─────────────────────────────────────────────────── */

/** Map flat CLI subcommands to browser tool names */
const TOOL_ALIASES: Record<string, string> = {
  navigate: 'navigate',
  back: 'navigate_back',
  snapshot: 'snapshot',
  screenshot: 'screenshot',
  evaluate: 'evaluate',
  'run-code': 'run_code',
  scan: 'scan',
  click: 'click',
  type: 'type',
  fill: 'fill_form',
  select: 'select_option',
  press: 'press_key',
  scroll: 'scroll',
  hover: 'hover',
  drag: 'drag',
  tabs: 'tabs',
  wait: 'wait_for',
  console: 'console_messages',
  network: 'network_requests',
  upload: 'file_upload',
  resize: 'resize',
  dismiss: 'dismiss_overlay',
  dialog: 'handle_dialog',
  downloads: 'list_downloads',
  'read-download': 'read_download',
  pdf: 'pdf_read',
};

/* ── Help ────────────────────────────────────────────────────────────── */

function printHelp() {
  console.log(
    [
      'Usage: canary session <subcommand> [options]',
      '',
      'Session lifecycle:',
      '  start [options]     Start a new browser session',
      '  list                List active sessions',
      '  status [<id>]       Show session details',
      '  stop [<id>]         Stop a session (or --all)',
      '  load-credential     Load a credential into a running session',
      '  save-credential     Save session login state back to its credential',
      '  allow-screenshots   Grant Claude auto-read access to session screenshots',
      '',
      'Control:',
      '  take-control [<id>]   Force CLI control (pauses cloud execution)',
      '  resume-cloud [<id>]   Resume paused cloud execution',
      '',
      'Browser actions:',
      '  navigate --url <url>                Navigate to URL',
      '  back                                Navigate back',
      '  snapshot [--search <text>] [--expand <ref>] [--mode tree|screenshot|both] [--refless]',
      '  screenshot [--full-page] [--label <text>]',
      '  evaluate --js <expression>          Evaluate JavaScript (in-page; reads only)',
      '  run-code --code <playwright> [--observe immediate|settle]   Run driver-side Playwright code (real events; for edits)',
      '  call-recipe --application <id> --slug <slug> [--args \'{"k":"v"}\'] [--vars \'{}\'] [--observe immediate|settle]   Run a property interaction recipe against this session',
      '              (discover recipes + their args with: cofactor application recipe list --application <id>)',
      '  click --ref <ref> [--element <desc>]',
      '  click --x <n> --y <n> [--element <desc>]',
      '  type --ref <ref> --text <text> [--submit]',
      '  fill --fields \'[{"ref":"e1","value":"test"}]\'',
      '  select --ref <ref> --value <val> [--element <desc>]',
      '  press --key <key>                   Press keyboard key',
      '  scroll --direction <dir> [--amount <n>]',
      '  hover --ref <ref> [--element <desc>]',
      '  drag --start-ref <ref> --end-ref <ref> [--start-element <desc>] [--end-element <desc>]',
      '  drag --start-x <n> --start-y <n> --end-x <n> --end-y <n>',
      '  tabs [--action list|new|close|select] [--index <n>]',
      '  wait --text <text> [--timeout <ms>]',
      '  console [--only-errors]             Show console messages',
      '  network                             Show network requests',
      '  upload --paths <p1,p2,...>          Upload one or more files',
      '  resize --width <n> --height <n>     Resize the browser viewport',
      '  dialog --action accept|dismiss [--prompt-text <text>]',
      '  dismiss [--strategy escape|close_button|backdrop|auto]',
      '  downloads                           List files downloaded this session',
      '  read-download [--id <n>]            Read a downloaded file (PDF text extracted)',
      '  pdf --url <url>                     Fetch a PDF and extract its text',
      '',
      'Start options:',
      '  --credential [<name|id>]  Use a credential (interactive if no value)',
      '  --storage-state <path>    Use a local storage state file',
      '  --headless                Run browser without visible UI',
      '  --url <url>               Navigate to URL after launch',
      '  --name <label>            Name the session',
      '  --cloud-relay             Connect session to cloud API for remote control',
      '  --attach-real-chrome      Attach to the user\'s running Chrome via chrome-devtools-mcp.',
      '                            Requires Chrome 144+ with remote debugging enabled at',
      '                            chrome://inspect/#remote-debugging. Uses the live profile —',
      '                            not compatible with --headless/--credential/--storage-state/--url.',
      '',
      'Load-credential options:',
      '  --credential [<name|id>]  Use a credential (interactive if no value)',
      '  --storage-state <path>    Use a local storage state file',
      '',
      'Save-credential options:',
      '  --credential [<name|id>]  Target credential (defaults to session\'s credential)',
      '',
      'Common options:',
      '  --session <id|name>       Target a specific session (when multiple)',
      '  --json                    Output raw JSON',
      '  --env <env>               Environment for credential resolution',
      '  --api-url <url>           API URL override (useful for isolated local workspaces)',
      '  --token <key>              API token override for credential resolution',
      '  -h, --help                Show this help',
    ].join('\n')
  );
}

/* ── Main entry ──────────────────────────────────────────────────────── */

export async function runSession(argv: string[]) {
  const [subcommand, ...rest] = argv;

  if (!subcommand || subcommand === 'help' || hasFlag(argv, '-h', '--help')) {
    printHelp();
    return;
  }

  // Internal: daemon process mode
  if (subcommand === 'daemon') {
    await runDaemon();
    return;
  }

  switch (subcommand) {
    case 'start':
      await handleStart(rest);
      break;
    case 'list':
      await handleList(rest);
      break;
    case 'status':
      await handleStatus(rest);
      break;
    case 'stop':
      await handleStop(rest);
      break;
    case 'load-credential':
      await handleLoadCredential(rest);
      break;
    case 'save-credential':
      await handleSaveCredential(rest);
      break;
    case 'allow-screenshots':
      handleAllowScreenshots();
      break;
    case 'take-control':
      await handleTakeControl(rest);
      break;
    case 'resume-cloud':
      await handleResumeCloud(rest);
      break;
    case 'call-recipe':
    case 'call_recipe':
      await handleCallRecipe(rest, printToolResult);
      break;
    default: {
      // Check for tool alias
      const toolName = TOOL_ALIASES[subcommand];
      if (toolName) {
        await handleToolCommand(toolName, rest);
      } else {
        console.error(`Unknown session subcommand: ${subcommand}`);
        printHelp();
        process.exitCode = 1;
      }
    }
  }
}
